/**
 * Cognito client wrapper.
 *
 * Centralizes amazon-cognito-identity-js calls so the rest of the app sees
 * a typed, promise-based API. Token storage policy:
 *
 *   - Access + ID tokens live ONLY in memory (this module's closure).
 *     They're regenerated from the refresh token on page reload via
 *     restoreSession().
 *   - Refresh token is what amazon-cognito-identity-js stores in
 *     localStorage. Cognito rotates it on each refresh.
 *
 * Decision context: see docs/backend/01-build-plan.md → Security Hardening
 * Roadmap. Phase 5 will migrate to httpOnly cookies via a token-relay Lambda.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// ─── Config ──────────────────────────────────────────────────────────────

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

if (!userPoolId || !clientId) {
  // In dev (vite dev server) this fires before VITE_* env is loaded.
  // We log instead of throwing so the bundle still works for non-auth pages.
  // Calls to signIn/etc. will throw a clear error if the pool isn't configured.
  console.warn(
    '[cognito-client] VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID are not set. ' +
      'Auth calls will fail until these are provided.'
  );
}

const userPool =
  userPoolId && clientId
    ? new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId })
    : null;

// ─── Types ───────────────────────────────────────────────────────────────

export interface CognitoClaims {
  sub: string;
  email: string;
  organizationId: string;
  role: 'super_admin' | 'auditor' | 'org_admin' | 'security_analyst' | 'viewer';
}

export interface SignInResult {
  claims: CognitoClaims;
  accessToken: string;
  idToken: string;
}

export interface SignInChallenge {
  type: 'NEW_PASSWORD_REQUIRED' | 'MFA_REQUIRED' | 'TOTP_REQUIRED';
  cognitoUser: CognitoUser;
}

export type SignInResponse =
  | { kind: 'success'; result: SignInResult }
  | { kind: 'challenge'; challenge: SignInChallenge };

// ─── In-memory token state ───────────────────────────────────────────────

let cachedAccessToken: string | null = null;
let cachedIdToken: string | null = null;

export function getAccessToken(): string | null {
  return cachedAccessToken;
}

export function getIdToken(): string | null {
  return cachedIdToken;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function requirePool(): CognitoUserPool {
  if (!userPool) {
    throw new Error(
      'Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.'
    );
  }
  return userPool;
}

function claimsFromSession(session: CognitoUserSession): CognitoClaims {
  const payload = session.getIdToken().decodePayload() as Record<
    string,
    unknown
  >;
  return {
    sub: String(payload.sub ?? ''),
    email: String(payload.email ?? ''),
    organizationId: String(
      payload['custom:organization_id'] ?? payload.organization_id ?? ''
    ),
    role: (payload['custom:role'] ?? payload.role ?? 'viewer') as CognitoClaims['role'],
  };
}

function cacheTokens(session: CognitoUserSession): SignInResult {
  const accessToken = session.getAccessToken().getJwtToken();
  const idToken = session.getIdToken().getJwtToken();
  cachedAccessToken = accessToken;
  cachedIdToken = idToken;
  return {
    claims: claimsFromSession(session),
    accessToken,
    idToken,
  };
}

function clearTokens(): void {
  cachedAccessToken = null;
  cachedIdToken = null;
}

// ─── Public API ──────────────────────────────────────────────────────────

/**
 * Sign in with username (email) + password using SRP.
 * Returns either a fully-authenticated session or a challenge that must
 * be resolved (new-password, MFA, TOTP).
 */
export function signIn(
  email: string,
  password: string
): Promise<SignInResponse> {
  const pool = requirePool();
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
  const auth = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise<SignInResponse>((resolve, reject) => {
    cognitoUser.authenticateUser(auth, {
      onSuccess: (session) => {
        resolve({ kind: 'success', result: cacheTokens(session) });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        resolve({
          kind: 'challenge',
          challenge: { type: 'NEW_PASSWORD_REQUIRED', cognitoUser },
        });
      },
      mfaRequired: () => {
        resolve({
          kind: 'challenge',
          challenge: { type: 'MFA_REQUIRED', cognitoUser },
        });
      },
      totpRequired: () => {
        resolve({
          kind: 'challenge',
          challenge: { type: 'TOTP_REQUIRED', cognitoUser },
        });
      },
    });
  });
}

/**
 * Complete a new-password challenge issued by Cognito (forced password reset).
 */
export function completeNewPasswordChallenge(
  cognitoUser: CognitoUser,
  newPassword: string
): Promise<SignInResult> {
  return new Promise<SignInResult>((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => resolve(cacheTokens(session)),
        onFailure: (err) => reject(err),
      }
    );
  });
}

/**
 * Submit a TOTP / SMS MFA code in response to a challenge.
 */
export function submitMfaCode(
  cognitoUser: CognitoUser,
  code: string,
  challengeType: 'MFA_REQUIRED' | 'TOTP_REQUIRED'
): Promise<SignInResult> {
  return new Promise<SignInResult>((resolve, reject) => {
    const mfaType = challengeType === 'TOTP_REQUIRED' ? 'SOFTWARE_TOKEN_MFA' : undefined;
    cognitoUser.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve(cacheTokens(session)),
        onFailure: (err) => reject(err),
      },
      mfaType
    );
  });
}

/**
 * Try to restore an active session from the refresh token held in localStorage
 * by amazon-cognito-identity-js. Returns null if no session is recoverable.
 *
 * Call this once on app boot.
 */
export function restoreSession(): Promise<SignInResult | null> {
  const pool = userPool;
  if (!pool) return Promise.resolve(null);

  const currentUser = pool.getCurrentUser();
  if (!currentUser) return Promise.resolve(null);

  return new Promise<SignInResult | null>((resolve) => {
    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        clearTokens();
        resolve(null);
        return;
      }
      resolve(cacheTokens(session));
    });
  });
}

/**
 * Sign out. Best-effort revokes the refresh token server-side via Cognito
 * (which makes the RT unusable anywhere — defense against a stolen RT)
 * before clearing local state. If the network call fails we still clear
 * local state so the user isn't stuck on the dashboard.
 *
 * Server-side revocation requires enable_token_revocation = true on the
 * Cognito user pool client (set in cognito.tf). Without that the call is
 * effectively a no-op and we degrade to a local-only signOut.
 */
export function signOut(): Promise<void> {
  const pool = userPool;
  if (!pool) {
    clearTokens();
    return Promise.resolve();
  }
  const currentUser = pool.getCurrentUser();
  if (!currentUser) {
    clearTokens();
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    // Need a valid session to call globalSignOut.
    currentUser.getSession((err: Error | null) => {
      if (err) {
        // No valid session — nothing to revoke, just clear local.
        try { currentUser.signOut(); } catch { /* best effort */ }
        clearTokens();
        resolve();
        return;
      }
      currentUser.globalSignOut({
        onSuccess: () => {
          clearTokens();
          resolve();
        },
        onFailure: () => {
          // Network or permission error — fall back to local signOut so the
          // user isn't trapped in the app.
          try { currentUser.signOut(); } catch { /* best effort */ }
          clearTokens();
          resolve();
        },
      });
    });
  });
}

/**
 * Global sign-out across all devices. Invalidates the refresh token server-side.
 * Used by admins to forcibly revoke a session.
 */
export function globalSignOut(): Promise<void> {
  const pool = requirePool();
  const currentUser = pool.getCurrentUser();
  if (!currentUser) {
    clearTokens();
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    currentUser.getSession((err: Error | null) => {
      if (err) {
        clearTokens();
        reject(err);
        return;
      }
      currentUser.globalSignOut({
        onSuccess: () => {
          clearTokens();
          resolve();
        },
        onFailure: (e) => {
          clearTokens();
          reject(e);
        },
      });
    });
  });
}
